// src/context/Notifications/notificationsCache.ts
import { AppNotification, NotificationType } from './types';

export const RTL_STORAGE_KEY = 'rtl_activity_log';
export const MAX_ENTRIES = 50;
export const MAX_AGE_DAYS = 7;
/** Relayed cut/tamper frames within this window collapse into one entry. */
export const DEDUPE_WINDOW_MS = 60 * 1000;

export const notificationStorageKey = (companyId: string): string =>
  `${RTL_STORAGE_KEY}:${encodeURIComponent(companyId)}`;

const SEAL_TYPES: NotificationType[] = ['cut', 'tamper'];

export const isSealType = (type: string): boolean =>
  (SEAL_TYPES as string[]).includes(type);

export const genNotifId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

/**
 * Parse a stored timestamp to epoch ms. Timestamps are stored as unix SECONDS
 * strings (e.g. "1717459200"); `new Date(string)` would parse those as Invalid
 * Date, so we detect the all-digit form explicitly. ISO strings are also handled.
 */
const toMillis = (timestamp: string): number => {
  const trimmed = (timestamp ?? '').trim();
  if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10) * 1000;
  const ms = new Date(trimmed).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

export const filterOldEntries = (
  entries: AppNotification[],
  now: number,
  maxAgeDays: number = MAX_AGE_DAYS
): AppNotification[] => {
  const cutoff = now - maxAgeDays * 24 * 60 * 60 * 1000;
  return entries.filter((e) => toMillis(e.timestamp) > cutoff);
};

export interface AppNotificationInput {
  device_id: string;
  type: string;
  value: string;
  /** unix seconds */
  timestamp: number;
}

export const ingest = (
  prev: AppNotification[],
  incoming: AppNotificationInput,
  now: number,
  makeId: () => string = genNotifId
): AppNotification[] => {
  const seal = isSealType(incoming.type);
  const entry: AppNotification = {
    notifId: makeId(),
    deviceId: incoming.device_id,
    type: incoming.type as NotificationType,
    // iMSecure cut/tamper carry a non-numeric value; don't coerce to NaN.
    value: seal ? incoming.value : Number(incoming.value),
    timestamp: String(incoming.timestamp),
    read: false,
  };
  const incomingMs = incoming.timestamp * 1000;
  const deduped = prev.filter(
    (n) =>
      !(
        n.deviceId === entry.deviceId &&
        n.type === entry.type &&
        Math.abs(incomingMs - toMillis(n.timestamp)) <= DEDUPE_WINDOW_MS
      )
  );
  return [entry, ...deduped].slice(0, MAX_ENTRIES);
};

interface RawEntry {
  notifId?: unknown;
  id?: unknown;
  deviceId?: unknown;
  value?: unknown;
  timestamp?: unknown;
  message?: unknown;
  type?: unknown;
  read?: unknown;
}

/**
 * Normalize whatever is in localStorage (legacy {id,...} shape or the current
 * AppNotification shape) into AppNotification[]. Legacy entries without a `read`
 * flag are treated as already-read so an upgrade doesn't surface a wall of
 * "unread" notifications.
 */
export const migrate = (
  raw: unknown,
  now: number,
  makeId: () => string = genNotifId
): AppNotification[] => {
  if (!Array.isArray(raw)) return [];
  const normalized: AppNotification[] = raw
    .filter((r): r is RawEntry => !!r && typeof r === 'object')
    .map((r) => ({
      notifId: typeof r.notifId === 'string' ? r.notifId : makeId(),
      deviceId:
        typeof r.deviceId === 'string'
          ? r.deviceId
          : typeof r.id === 'string'
          ? r.id
          : '',
      type: (typeof r.type === 'string' ? r.type : 'shock') as NotificationType,
      value:
        typeof r.value === 'number' || typeof r.value === 'string'
          ? r.value
          : '',
      timestamp: typeof r.timestamp === 'string' ? r.timestamp : '0',
      message: typeof r.message === 'string' ? r.message : undefined,
      read: typeof r.read === 'boolean' ? r.read : true,
    }));
  return filterOldEntries(normalized, now).slice(0, MAX_ENTRIES);
};

export const markRead = (
  entries: AppNotification[],
  notifId: string
): AppNotification[] =>
  entries.map((e) => (e.notifId === notifId ? { ...e, read: true } : e));

export const markAllRead = (entries: AppNotification[]): AppNotification[] =>
  entries.map((e) => (e.read ? e : { ...e, read: true }));

export const unreadCount = (entries: AppNotification[]): number =>
  entries.reduce((n, e) => (e.read ? n : n + 1), 0);
